/**
 * Search Modal Component
 * WCAG AA compliant modal dialog for search functionality
 */

export function initSearchModal() {
    const searchButtons = document.querySelectorAll('[data-search-trigger]');
    let lastFocusedElement: HTMLElement | null = null;
    
    // Get modal elements
    const overlay = document.querySelector('[data-search-overlay]') as HTMLElement | null;
    const modal = document.querySelector('.search-modal-dialog') as HTMLElement | null;
    const searchInput = document.querySelector('#search-input') as HTMLInputElement | null;
    const closeButton = document.querySelector('[data-search-close]') as HTMLButtonElement | null;

    if (!overlay || !modal || !searchInput || !closeButton) {
        console.error('Search modal elements not found');
        return;
    }

    // Open modal
    function openModal(): void {
        if (!overlay) return;
        
        // Store the last focused element
        lastFocusedElement = document.activeElement as HTMLElement;

        // Show modal
        overlay.classList.add('active');
        overlay.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';

        // Focus search input with a small delay for screen readers
        setTimeout(() => {
            searchInput?.focus();
        }, 100);
    }

    // Close modal
    function closeModal(): void {
        if (!overlay) return;
        
        overlay.classList.remove('active');
        overlay.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';

        // Return focus to trigger button
        if (lastFocusedElement) {
            lastFocusedElement.focus();
        }
    }

    // Handle ESC key
    function handleEscKey(e: KeyboardEvent): void {
        if (e.key === 'Escape' && overlay?.classList.contains('active')) {
            closeModal();
        }
    }

    // Focus trap implementation
    function handleFocusTrap(e: KeyboardEvent): void {
        if (e.key !== 'Tab' || !modal) return;

        const focusableElements = modal.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstFocusable = focusableElements[0] as HTMLElement;
        const lastFocusable = focusableElements[focusableElements.length - 1] as HTMLElement;

        // Shift + Tab
        if (e.shiftKey) {
            if (document.activeElement === firstFocusable) {
                lastFocusable.focus();
                e.preventDefault();
            }
        } 
        // Tab
        else {
            if (document.activeElement === lastFocusable) {
                firstFocusable.focus();
                e.preventDefault();
            }
        }
    }

    // Set up event listeners
    closeButton.addEventListener('click', closeModal);

    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
            closeModal();
        }
    });

    // ESC key handler
    document.addEventListener('keydown', handleEscKey);

    // Focus trap
    modal.addEventListener('keydown', handleFocusTrap);

    // Initialize search buttons
    searchButtons.forEach(button => {
        button.addEventListener('click', (e) => {
            e.preventDefault();
            openModal();
        });

        // Ensure buttons are keyboard accessible
        button.addEventListener('keydown', (e: Event) => {
            const keyEvent = e as KeyboardEvent;
            if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
                e.preventDefault();
                openModal();
            }
        });
    });

    return {
        open: openModal,
        close: closeModal
    };
}
